import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from './ui/select';
import {
  Receipt, ShoppingCart, Building2, Landmark, Calendar, ArrowUpCircle,
  Upload, FilePlus2, Eye, Download, X, Megaphone, Palette, Printer,
  Wrench, Fuel, Home, Users, Wifi, CreditCard, Package, Trash2,
} from 'lucide-react';
import ImportFacturePDFDialog from './ImportFacturePDFDialog';
import BulkImportFacturePDFDialog from './BulkImportFacturePDFDialog';
import SearchBox from './SearchBox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Catégories métier (régie publicitaire / location de panneaux)
// Chaque catégorie appartient à un groupe : revenue | charge | invest | impot
const CATEGORIES = {
  // ---- REVENUS (Ventes clients) ----
  vente_location:    { group: 'revenue', label: 'Location de panneau',         icon: Megaphone,  badgeCls: 'bg-emerald-100 text-emerald-700' },
  vente_creation:    { group: 'revenue', label: 'Création / Production',       icon: Palette,    badgeCls: 'bg-teal-100 text-teal-700' },
  vente_regie:       { group: 'revenue', label: 'Régie publicitaire',          icon: Receipt,    badgeCls: 'bg-emerald-100 text-emerald-700' },
  client:            { group: 'revenue', label: 'Vente (autre)',               icon: Receipt,    badgeCls: 'bg-emerald-100 text-emerald-700' },

  // ---- CHARGES (Fournisseurs / dépenses payées comptant) ----
  achat_impression:  { group: 'charge',  label: 'Impression / Affichage',      icon: Printer,    badgeCls: 'bg-orange-100 text-orange-700' },
  achat_maintenance: { group: 'charge',  label: 'Maintenance panneaux',        icon: Wrench,     badgeCls: 'bg-amber-100 text-amber-700' },
  achat_carburant:   { group: 'charge',  label: 'Carburant / Transport',       icon: Fuel,       badgeCls: 'bg-yellow-100 text-yellow-800' },
  charges_loyer:     { group: 'charge',  label: 'Loyer & Charges locales',     icon: Home,       badgeCls: 'bg-pink-100 text-pink-700' },
  charges_salaires:  { group: 'charge',  label: 'Salaires & Personnel',        icon: Users,      badgeCls: 'bg-indigo-100 text-indigo-700' },
  charges_telecom:   { group: 'charge',  label: 'Télécom / Internet',          icon: Wifi,       badgeCls: 'bg-cyan-100 text-cyan-700' },
  charges_banque:    { group: 'charge',  label: 'Frais bancaires',             icon: CreditCard, badgeCls: 'bg-stone-100 text-stone-700' },
  fournisseur:       { group: 'charge',  label: 'Achat fournisseur (payé comptant)', icon: ShoppingCart, badgeCls: 'bg-orange-100 text-orange-700' },

  // ---- DETTE FOURNISSEUR (achat NON payé) ----
  dette_fournisseur: { group: 'dette',   label: 'Dette fournisseur (à payer)', icon: ShoppingCart, badgeCls: 'bg-red-100 text-red-700' },

  // ---- INVESTISSEMENTS ----
  invest_panneau:    { group: 'invest',  label: 'Achat panneau / Support',     icon: Building2,  badgeCls: 'bg-purple-100 text-purple-700' },
  invest_vehicule:   { group: 'invest',  label: 'Véhicule / Matériel',         icon: Package,    badgeCls: 'bg-violet-100 text-violet-700' },
  investissement:    { group: 'invest',  label: 'Investissement (autre)',      icon: Building2,  badgeCls: 'bg-purple-100 text-purple-700' },

  // ---- IMPÔTS & TAXES ----
  impot:             { group: 'impot',   label: 'Impôt / Taxe',                icon: Landmark,   badgeCls: 'bg-rose-100 text-rose-700' },
};

const GROUPS = {
  revenue: { label: 'Revenus',         color: 'text-emerald-700', sign: '+', target: 'Compte de résultat + Trésorerie' },
  charge:  { label: 'Charges payées',  color: 'text-rose-700',    sign: '-', target: 'Compte de résultat + Trésorerie' },
  dette:   { label: 'Dettes à payer',  color: 'text-red-700',     sign: '-', target: 'Compte de résultat + Bilan (Passif)' },
  invest:  { label: 'Investissements', color: 'text-violet-700',  sign: '-', target: 'Bilan (Actif immobilisé) + Trésorerie' },
  impot:   { label: 'Impôts & Taxes',  color: 'text-rose-700',    sign: '-', target: 'Compte de résultat + Trésorerie' },
};

// Mapping anciennes catégories → nouvelle (rétrocompat avec factures existantes)
const LEGACY_MAP = { client: 'client', fournisseur: 'fournisseur', investissement: 'investissement', impot: 'impot' };

const fmtFCFA = (v) => (Number(v) || 0).toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
};

function getCat(f) {
  const c = f.categorie || 'client';
  return CATEGORIES[c] ? c : (LEGACY_MAP[c] || 'client');
}

export default function FacturesComptaTab() {
  const [factures, setFactures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterGroup, setFilterGroup] = useState('all');
  const [filterCat, setFilterCat] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [previewFacture, setPreviewFacture] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/factures`);
      const list = Array.isArray(data) ? data : (data?.items || []);
      const sorted = [...list].sort((a, b) => (b.date_facture || '').localeCompare(a.date_facture || ''));
      setFactures(sorted);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateCategorie = async (factureId, newCat) => {
    try {
      await axios.put(`${API}/api/factures/${factureId}`, { categorie: newCat });
      toast.success('Catégorie mise à jour — Trésorerie & Compte de résultat synchronisés');
      load();
    } catch { toast.error('Erreur lors de la mise à jour'); }
  };

  const deleteFacture = async (facture) => {
    if (!window.confirm(`Supprimer définitivement la facture ${facture.numero} ?\n\nLes écritures associées dans la Trésorerie, le Compte de résultat et le Bilan seront également supprimées.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/api/factures/${facture.id}`);
      toast.success(`Facture ${facture.numero} supprimée — Tableaux synchronisés`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const filtered = factures.filter(f => {
    const cat = getCat(f);
    const grp = CATEGORIES[cat]?.group || 'revenue';
    if (filterGroup !== 'all' && grp !== filterGroup) return false;
    if (filterCat !== 'all' && cat !== filterCat) return false;
    if (dateFrom && (f.date_facture || '') < dateFrom) return false;
    if (dateTo && (f.date_facture || '') > dateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!`${f.numero || ''} ${f.objet || ''} ${f.client?.nom || ''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Totaux par groupe (pour KPI cards)
  const totalsByGroup = filtered.reduce((acc, f) => {
    const grp = CATEGORIES[getCat(f)]?.group || 'revenue';
    acc[grp] = (acc[grp] || 0) + (f.total_ttc || 0);
    return acc;
  }, {});

  // Groupement par mois
  const groupedByMonth = filtered.reduce((acc, f) => {
    const d = f.date_facture || '';
    const key = d ? d.slice(0, 7) : 'sans-date';
    if (!acc[key]) acc[key] = [];
    acc[key].push(f);
    return acc;
  }, {});

  const handlePreview = (f) => {
    // On utilise directement l'URL backend avec le token en query string.
    // Cette approche fonctionne dans iframe/object sur TOUS les navigateurs
    // (contrairement aux blob URLs et data URLs qui sont parfois bloquées).
    const token = localStorage.getItem('token') || '';
    setPreviewUrl(`${API}/api/factures/${f.id}/pdf?token=${encodeURIComponent(token)}`);
    setPreviewFacture(f);
  };

  const handleDownload = async (f) => {
    try {
      const { data } = await axios.get(`${API}/api/factures/${f.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${f.numero || 'facture'}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Impossible de télécharger le PDF'); }
  };

  const closePreview = () => {
    setPreviewUrl(null);
    setPreviewFacture(null);
  };

  // Catégories filtrées par groupe choisi (pour le select Catégorie)
  const filteredCatOptions = Object.entries(CATEGORIES).filter(
    ([, m]) => filterGroup === 'all' || m.group === filterGroup
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-[#1C2A46]">Factures</h2>
          <p className="text-sm text-slate-500 mt-0.5">Toutes les factures classées par catégorie métier.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setBulkOpen(true)} variant="outline" className="gap-2" data-testid="btn-bulk-import">
            <FilePlus2 className="h-4 w-4" /> Import groupé
          </Button>
          <Button onClick={() => setImportOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="btn-import-pdf">
            <Upload className="h-4 w-4" /> Importer un PDF
          </Button>
        </div>
      </div>

      <ImportFacturePDFDialog open={importOpen} onOpenChange={setImportOpen} onImported={load} />
      <BulkImportFacturePDFDialog open={bulkOpen} onOpenChange={setBulkOpen} onImported={load} />

      {/* KPI Cards par groupe */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(GROUPS).map(([key, g]) => {
          const total = totalsByGroup[key] || 0;
          const count = filtered.filter(f => CATEGORIES[getCat(f)]?.group === key).length;
          return (
            <Card
              key={key}
              className={`border cursor-pointer transition-all ${filterGroup === key ? 'ring-2 ring-[#1C2A46] border-[#1C2A46]' : 'border-slate-200'}`}
              onClick={() => { setFilterGroup(filterGroup === key ? 'all' : key); setFilterCat('all'); }}
              data-testid={`kpi-group-${key}`}
            >
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-slate-500">{g.label}</p>
                  <Badge variant="outline" className="text-[10px]">{count}</Badge>
                </div>
                <p className={`text-base sm:text-lg font-bold ${g.color}`}>
                  {g.sign}{fmtFCFA(total)}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtres */}
      <Card className="border border-slate-200">
        <CardContent className="p-3 sm:p-4 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="Rechercher (n°, objet, client…)"
              color="blue"
              testIdPrefix="factures-search"
              className="w-full"
            />
            <Select
              value={filterCat}
              onValueChange={(v) => {
                setFilterCat(v);
                if (v !== 'all') setFilterGroup(CATEGORIES[v]?.group || 'all');
              }}
            >
              <SelectTrigger className="h-9 text-sm" data-testid="filter-categorie">
                <SelectValue placeholder="Toutes les catégories" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                <SelectItem value="all">Toutes les catégories</SelectItem>
                {Object.entries(GROUPS).map(([gKey, g]) => {
                  const cats = filteredCatOptions.filter(([, m]) => m.group === gKey);
                  if (cats.length === 0) return null;
                  return (
                    <SelectGroup key={gKey}>
                      <SelectLabel className="text-xs text-slate-500">{g.label}</SelectLabel>
                      {cats.map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 text-sm"
                placeholder="Du"
                data-testid="filter-date-from"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-slate-400 text-sm">→</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9 text-sm"
                placeholder="Au"
                data-testid="filter-date-to"
              />
            </div>
          </div>
          <p className="text-xs text-slate-500 text-right">
            {filtered.length} facture{filtered.length > 1 ? 's' : ''}
            {(filterGroup !== 'all' || filterCat !== 'all' || dateFrom || dateTo || search) && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-2 h-6 px-2 text-xs"
                onClick={() => { setFilterGroup('all'); setFilterCat('all'); setDateFrom(''); setDateTo(''); setSearch(''); }}
              >
                Réinitialiser
              </Button>
            )}
          </p>
        </CardContent>
      </Card>

      {/* Liste groupée par mois */}
      {loading ? (
        <p className="text-sm text-slate-500">Chargement…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-8">Aucune facture trouvée.</p>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByMonth).map(([month, list]) => (
            <div key={month}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-slate-500" />
                <h3 className="text-sm font-bold text-slate-700">
                  {month === 'sans-date'
                    ? 'Sans date'
                    : new Date(month + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                </h3>
                <Badge variant="outline" className="text-[10px]">{list.length}</Badge>
              </div>
              <div className="space-y-2">
                {list.map(f => <FactureRow
                  key={f.id}
                  facture={f}
                  onChangeCat={(v) => updateCategorie(f.id, v)}
                  onPreview={() => handlePreview(f)}
                  onDownload={() => handleDownload(f)}
                  onDelete={() => deleteFacture(f)}
                />)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Légende */}
      <Card className="border border-blue-100 bg-blue-50/30">
        <CardContent className="p-3 text-xs text-slate-600">
          <p className="font-semibold text-slate-700 flex items-center gap-2">
            <ArrowUpCircle className="h-4 w-4 text-emerald-600" /> Auto-comptabilité activée
          </p>
          <p className="mt-1">Chaque facture créée est automatiquement inscrite dans la trésorerie et/ou le bilan selon sa catégorie métier.</p>
        </CardContent>
      </Card>

      {/* Modal Preview PDF */}
      {previewFacture && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="min-w-0">
                <p className="font-bold text-[#1C2A46] truncate flex items-center gap-2">
                  {previewFacture.numero}
                  {previewFacture.has_source_pdf ? (
                    <Badge className="text-[10px] bg-blue-100 text-blue-700">📎 PDF d'origine</Badge>
                  ) : (
                    <Badge className="text-[10px] bg-amber-100 text-amber-700">📄 PDF généré (template)</Badge>
                  )}
                </p>
                <p className="text-xs text-slate-500">{previewFacture.objet || 'Facture'} · {fmtDate(previewFacture.date_facture)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => window.open(previewUrl, '_blank')} className="gap-1.5" data-testid="preview-newtab">
                  <Eye className="h-4 w-4" /> Nouvel onglet
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDownload(previewFacture)} className="gap-1.5" data-testid="preview-download">
                  <Download className="h-4 w-4" /> Télécharger
                </Button>
                <Button size="sm" variant="ghost" onClick={closePreview} aria-label="Fermer">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Bandeau d'avertissement si pas de PDF original */}
            {!previewFacture.has_source_pdf && (
              <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 text-amber-900 text-xs flex items-start gap-2">
                <span className="text-base leading-none">ℹ️</span>
                <div>
                  <strong>Ceci n'est pas le PDF d'origine</strong> — il s'agit d'un PDF généré automatiquement à partir des données extraites.
                  Pour récupérer le PDF original, supprimez cette facture et <strong>réimportez le PDF</strong> via le bouton « Importer un PDF ».
                </div>
              </div>
            )}

            <div className="flex-1 w-full bg-slate-100 overflow-hidden">
              <object data={previewUrl} type="application/pdf" className="w-full h-full">
                <iframe src={previewUrl} title="Facture PDF" className="w-full h-full border-0" />
                <div className="flex flex-col items-center justify-center h-full text-center p-6 gap-3">
                  <p className="text-slate-600">Votre navigateur ne peut pas afficher ce PDF directement.</p>
                  <Button onClick={() => handleDownload(previewFacture)} className="gap-2">
                    <Download className="h-4 w-4" /> Télécharger le PDF
                  </Button>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FactureRow({ facture, onChangeCat, onPreview, onDownload, onDelete }) {
  const cat = getCat(facture);
  const meta = CATEGORIES[cat];
  const grp = GROUPS[meta?.group || 'revenue'];
  const Icon = meta?.icon || Receipt;

  const hasCurrencyMeta = !!(facture.currency_meta?.original_currency && facture.currency_meta?.rate_to_xaf);

  const handleAuditPdf = async () => {
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      const axios = (await import('axios')).default;
      const { data } = await axios.get(`${API}/api/factures/${facture.id}/pdf?force_sesam=1`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Card className="border border-slate-200 hover:shadow-md transition-all" data-testid={`facture-row-${facture.id}`}>
      <CardContent className="p-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta?.badgeCls || 'bg-slate-100'}`}>
            <Icon className="h-5 w-5" />
          </div>

          {/* Date proéminente */}
          <div className="flex-shrink-0 text-center bg-slate-50 rounded-md px-3 py-1.5 min-w-[80px]">
            <div className="text-[10px] text-slate-500 uppercase">Date</div>
            <div className="text-sm font-bold text-[#1C2A46]">{fmtDate(facture.date_facture)}</div>
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm text-[#1C2A46]">{facture.numero}</p>
            <p className="text-xs text-slate-600 mt-0.5 truncate">
              {facture.client?.nom || '—'} · {facture.objet || 'Facturation'}
            </p>
          </div>

          <div className="text-right flex-shrink-0">
            <p className={`text-base font-bold ${grp?.color || 'text-slate-700'}`}>
              {grp?.sign || ''}{fmtFCFA(facture.total_ttc)}
            </p>
            <p className="text-[10px] text-slate-400 truncate max-w-[200px]">→ {grp?.target}</p>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              size="sm" variant="outline"
              onClick={onPreview}
              className="h-8 px-2 gap-1"
              data-testid={`preview-${facture.id}`}
              title="Aperçu PDF"
            >
              <Eye className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">Aperçu</span>
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={onDownload}
              className="h-8 px-2 gap-1"
              data-testid={`download-${facture.id}`}
              title="Télécharger PDF"
            >
              <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">PDF</span>
            </Button>
            {hasCurrencyMeta && (
              <Button
                size="sm" variant="outline"
                onClick={handleAuditPdf}
                className="h-8 px-2 gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid={`audit-pdf-${facture.id}`}
                title="PDF audit avec mention légale de conversion de devise (OHADA/SYSCOHADA)"
              >
                💱 <span className="hidden sm:inline text-xs">Audit</span>
              </Button>
            )}
            <Button
              size="sm" variant="outline"
              onClick={onDelete}
              className="h-8 px-2 gap-1 text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              data-testid={`delete-${facture.id}`}
              title="Supprimer la facture"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
            <Select value={cat} onValueChange={onChangeCat}>
              <SelectTrigger className="h-8 w-[180px] text-xs" data-testid={`change-cat-${facture.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {Object.entries(GROUPS).map(([gKey, g]) => {
                  const cats = Object.entries(CATEGORIES).filter(([, m]) => m.group === gKey);
                  return (
                    <SelectGroup key={gKey}>
                      <SelectLabel className="text-xs text-slate-500">{g.label}</SelectLabel>
                      {cats.map(([k, m]) => (
                        <SelectItem key={k} value={k}>{m.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
